import { AuthError, type AuthTokens, type SessionBroker } from '../contract/auth.js';

/**
 * B-AUTH session broker client. All calls carry credentials (the HttpOnly
 * cookie) and the anti-CSRF header. `base` is '' in local dev (Vite proxies
 * /session* for same-site cookie behavior) and the API origin in production,
 * where app and API are naturally same-site.
 */
export class HttpSessionBroker implements SessionBroker {
  constructor(private readonly base: string) {}

  private call(path: string, body?: unknown): Promise<Response> {
    return fetch(`${this.base}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'x-gm-csrf': '1',
        ...(body !== undefined && { 'content-type': 'application/json' }),
      },
      ...(body !== undefined && { body: JSON.stringify(body) }),
    });
  }

  async adopt(refreshToken: string): Promise<void> {
    let res: Response;
    try {
      res = await this.call('/session', { refreshToken });
    } catch {
      throw new AuthError('NETWORK', 'Network problem. Check your connection and try again.');
    }
    if (res.status !== 204) {
      throw new AuthError('UNKNOWN', 'Could not establish your session. Try signing in again.');
    }
  }

  async refresh(): Promise<AuthTokens | undefined> {
    let res: Response;
    try {
      res = await this.call('/session/refresh');
    } catch {
      throw new AuthError('NETWORK', 'Network problem. Check your connection and try again.');
    }
    if (res.status !== 200) return undefined; // stable 401 contract: no session
    const body = (await res.json()) as AuthTokens;
    return { accessToken: body.accessToken, idToken: body.idToken, expiresIn: body.expiresIn };
  }

  async signOut(): Promise<void> {
    try {
      await this.call('/session/signout');
    } catch {
      // Network failure must not strand the user signed-in client-side; the
      // cookie-backed session dies at expiry and tokens are already dropped.
    }
  }
}
